import { useCallback, useEffect, useState } from 'react';
import type { ProjectSummaryDto } from '@scs/schemas';
import { Api } from '../lib/api';
import { navigate } from '../lib/router';
import { getLang, t } from '../lib/i18n';
import { Logo } from '../lib/ui';
import { TopbarUser, useSession } from '../App';

type View = 'active' | 'archived' | 'trash';

const STATUS_LABEL: Record<ProjectSummaryDto['status'], string> = {
  draft: 'Nacrt',
  quoted: 'Ponuda',
  approved: 'Odobreno',
  'in-production': 'U proizvodnji',
  done: 'Završeno',
};

export function DashboardPage() {
  const { me } = useSession();
  const canEdit = me.workspace.role !== 'viewer';
  const [view, setView] = useState<View>('active');
  const [q, setQ] = useState('');
  const [items, setItems] = useState<ProjectSummaryDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<ProjectSummaryDto | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      setItems(await Api.projects(view, q));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [view, q]);

  useEffect(() => {
    const id = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(id);
  }, [load, q]);

  const act = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const fmt = new Intl.DateTimeFormat(getLang() === 'sr' ? 'sr-Latn-BA' : 'en-GB', { dateStyle: 'medium', timeStyle: 'short' });

  return (
    <div>
      <header className="topbar">
        <div className="brand">
          <Logo /> {t('app.name')}
        </div>
        <span className="muted">{me.workspace.name}</span>
        <button className="btn ghost sm" onClick={() => navigate('/presets')}>
          Preseti
        </button>
        <button className="btn ghost sm" onClick={() => navigate('/catalog')}>
          Katalog
        </button>
        {(me.user.isSystemAdmin || me.workspace.role === 'owner' || me.workspace.role === 'admin') && (
          <button className="btn ghost sm" onClick={() => navigate('/admin')}>
            Admin
          </button>
        )}
        <div className="spacer" />
        <TopbarUser />
      </header>
      <main className="dash">
        <div className="dash-head">
          <h1>{t('dash.projects')}</h1>
          <div className="tabs" role="tablist">
            {(['active', 'archived', 'trash'] as View[]).map((v) => (
              <button key={v} role="tab" aria-selected={view === v} className={view === v ? 'on' : ''} onClick={() => setView(v)}>
                {t(`dash.${v}` as const)}
              </button>
            ))}
          </div>
          <input className="input" style={{ flex: '1 1 220px', maxWidth: 360 }} placeholder={t('dash.search')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('dash.search')} />
          <div style={{ flex: 1 }} />
          {canEdit && (
            <button className="btn primary" onClick={() => setCreating(true)}>
              + {t('dash.new')}
            </button>
          )}
        </div>
        {error && <p className="error-text">{error}</p>}
        {items && items.length === 0 && <p className="muted">{t('dash.empty')}</p>}
        <div className="proj-grid">
          {items?.map((p) => (
            <article key={p.id} className="card proj-card">
              <div className="proj-thumb" onClick={() => navigate(`/p/${p.id}`)} role="button" aria-label={`${t('dash.open')} ${p.name}`}>
                <CabinetGlyph />
              </div>
              <div className="proj-body">
                <div className="proj-title">
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                  {p.isDemo && <span className="badge demo">{t('dash.demo')}</span>}
                </div>
                <div className="muted">{p.client || '—'}</div>
                <div className="proj-meta">
                  <span>{STATUS_LABEL[p.status]}</span>
                  <span>
                    {t('dash.revision')} {p.headRevision} · {fmt.format(new Date(p.updatedAt))}
                  </span>
                </div>
              </div>
              <div className="proj-actions">
                {view !== 'trash' && (
                  <button className="btn sm" onClick={() => navigate(`/p/${p.id}`)}>
                    {t('dash.open')}
                  </button>
                )}
                {canEdit && view === 'active' && (
                  <>
                    <button className="btn sm ghost" onClick={() => act(() => Api.duplicateProject(p.id))}>
                      {t('dash.duplicate')}
                    </button>
                    <button className="btn sm ghost" onClick={() => setRenaming(p)}>
                      {t('dash.rename')}
                    </button>
                    <button className="btn sm ghost" onClick={() => act(() => Api.patchProject(p.id, { archived: true }))}>
                      {t('dash.archive')}
                    </button>
                  </>
                )}
                {canEdit && view === 'archived' && (
                  <button className="btn sm ghost" onClick={() => act(() => Api.patchProject(p.id, { archived: false }))}>
                    {t('dash.unarchive')}
                  </button>
                )}
                {canEdit && view !== 'trash' && (
                  <button className="btn sm ghost danger" onClick={() => act(() => Api.deleteProject(p.id))}>
                    {t('dash.delete')}
                  </button>
                )}
                {canEdit && view === 'trash' && (
                  <button className="btn sm" onClick={() => act(() => Api.restoreProject(p.id))}>
                    {t('dash.restore')}
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      </main>
      {creating && (
        <NameModal
          title={t('dash.new')}
          withClient
          onClose={() => setCreating(false)}
          onSubmit={async (name, client) => {
            const p = await Api.createProject(name, client);
            navigate(`/p/${p.id}`);
          }}
        />
      )}
      {renaming && (
        <NameModal
          title={t('dash.rename')}
          initialName={renaming.name}
          initialClient={renaming.client}
          withClient
          onClose={() => setRenaming(null)}
          onSubmit={async (name, client) => {
            await Api.patchProject(renaming.id, { name, client });
            setRenaming(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

function NameModal(props: { title: string; initialName?: string; initialClient?: string; withClient?: boolean; onClose(): void; onSubmit(name: string, client: string): Promise<void> }) {
  const [name, setName] = useState(props.initialName ?? '');
  const [client, setClient] = useState(props.initialClient ?? '');
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="modal-backdrop" onClick={props.onClose}>
      <form
        className="card modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await props.onSubmit(name.trim(), client.trim());
          } catch (err) {
            setError((err as Error).message);
          }
        }}
      >
        <h2>{props.title}</h2>
        <label className="field">
          <span>{t('dash.name')}</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} autoFocus />
        </label>
        {props.withClient && (
          <label className="field">
            <span>{t('dash.client')}</span>
            <input className="input" value={client} onChange={(e) => setClient(e.target.value)} maxLength={200} />
          </label>
        )}
        {error && <p className="error-text">{error}</p>}
        <div className="actions">
          <button type="button" className="btn" onClick={props.onClose}>
            {t('dash.cancel')}
          </button>
          <button className="btn primary">{t('dash.create')}</button>
        </div>
      </form>
    </div>
  );
}

function CabinetGlyph() {
  return (
    <svg width="96" height="96" viewBox="0 0 96 96" aria-hidden>
      <rect x="14" y="10" width="68" height="74" rx="3" fill="none" stroke="var(--border-strong)" strokeWidth="2" />
      <line x1="48" y1="12" x2="48" y2="82" stroke="var(--border-strong)" strokeWidth="2" />
      <circle cx="43" cy="47" r="2" fill="var(--accent)" />
      <circle cx="53" cy="47" r="2" fill="var(--accent)" />
      <rect x="18" y="84" width="6" height="4" fill="var(--border-strong)" />
      <rect x="72" y="84" width="6" height="4" fill="var(--border-strong)" />
    </svg>
  );
}
